import { faroTableComparator } from "@components/common/faro-table/faro-table-utils";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { TransactionProject } from "@components/table/transactions/transaction-project";
import { TransactionUser } from "@components/table/transactions/transaction-user";
import {
  TransactionsHeaders,
  TransactionsHeadersType,
  getAmountSymbol,
  getTransactionAmount,
  getTransactionDescription,
  isConsumedTransaction,
  isExpiredTransaction,
  isFailedOrRejectedTransaction,
  isInsertedTransaction,
} from "@components/table/transactions/transactions-table-utils";
import { CreditTransaction } from "@custom-types/credit-types";
import { BaseMembersMapProps } from "@custom-types/member-types";
import { BaseProjectsMapProps } from "@custom-types/project-types";
import { Chip, green, neutral } from "@flat-ui";
import { FormatDate } from "@hooks/use-date-time";
import { Box } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  gridStringOrNumberComparator,
} from "@mui/x-data-grid-pro";
import { CREDIT_DISPLAY_NAME } from "@src/constants/credit-constants";
import { withEllipsis } from "@styles/common-styles";
import { nounPluralize } from "@utils/data-display";
import { resolveUserIdentity } from "@utils/user-utils";
import { DateTime } from "luxon";

interface Props extends BaseProjectsMapProps, BaseMembersMapProps {
  /** The function to format date. Needs to be passed since getMembersColumns is not a hook */
  formatDate: FormatDate;
}

/** Return all the possible columns for Transactions table */
export function getTransactionsColumns({
  formatDate,
  membersMap,
  projectsMap,
}: Props): Record<TransactionsHeadersType, GridColDef> {
  return {
    [TransactionsHeaders.date]: {
      field: TransactionsHeaders.date,
      flex: 0.5,
      minWidth: 180,
      renderCell: (
        params: GridRenderCellParams<{ entity: CreditTransaction }>
      ) => {
        return (
          <var>
            {formatDate(params.row.entity.timestamp, DateTime.DATE_MED)}
          </var>
        );
      },
      valueGetter: (_, row: { entity: CreditTransaction }) =>
        Date.parse(row.entity.timestamp),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [TransactionsHeaders.user]: {
      field: TransactionsHeaders.user,
      flex: 1,
      minWidth: 220,
      renderCell: (
        params: GridRenderCellParams<{ entity: CreditTransaction }>
      ) => {
        const userIdentity = resolveUserIdentity(
          params.row.entity.context.userId,
          params.row.entity.context.userEmail
        );

        const member = userIdentity ? membersMap.get(userIdentity) : undefined;

        return <TransactionUser member={member} />;
      },
      valueGetter: (_, row: { entity: CreditTransaction }) => {
        const userIdentity = resolveUserIdentity(
          row.entity.context.userId,
          row.entity.context.userEmail
        );

        const member = userIdentity ? membersMap.get(userIdentity) : undefined;

        return member?.name ?? userIdentity;
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [TransactionsHeaders.description]: {
      field: TransactionsHeaders.description,
      flex: 1,
      minWidth: 220,
      renderCell: (
        params: GridRenderCellParams<{ entity: CreditTransaction }>
      ) => (
        <SphereTooltip
          title={getTransactionDescription(params.row.entity)}
          boxProps={{
            sx: { ...withEllipsis },
          }}
        >
          {getTransactionDescription(params.row.entity)}
        </SphereTooltip>
      ),
      valueGetter: (_, row: { entity: CreditTransaction }) =>
        getTransactionDescription(row.entity),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [TransactionsHeaders.project]: {
      field: TransactionsHeaders.project,
      flex: 1.3,
      minWidth: 220,
      renderCell: (
        params: GridRenderCellParams<{ entity: CreditTransaction }>
      ) => {
        return (
          <TransactionProject
            projectId={params.row.entity.context.projectId ?? undefined}
          />
        );
      },
      valueGetter: (_, row: { entity: CreditTransaction }) => {
        return projectsMap.get(row.entity.context.projectId || "")?.name;
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [TransactionsHeaders.credits]: {
      field: TransactionsHeaders.credits,
      flex: 0.5,
      minWidth: 180,
      renderCell: (
        params: GridRenderCellParams<{ entity: CreditTransaction }>
      ) => {
        const isInserted = isInsertedTransaction(params.row.entity);
        const tokenAmount = getTransactionAmount(params.row.entity);

        return (
          <Chip
            disabled={isFailedOrRejectedTransaction(params.row.entity)}
            label={
              <Box sx={{ fontSize: "10px", fontWeight: 700 }}>
                <var style={{ fontWeight: "inherit" }}>
                  {getAmountSymbol(params.row.entity)}
                  {}
                </var>
                {nounPluralize({
                  shouldShowCounter: true,
                  counter: tokenAmount,
                  word: CREDIT_DISPLAY_NAME,
                })}
              </Box>
            }
            size="medium"
            variant="outlined"
            backgroundColor={isInserted ? green[50] : neutral[50]}
            borderColor={isInserted ? green[100] : neutral[100]}
          />
        );
      },
      valueGetter: (_, row: { entity: CreditTransaction }) => {
        const tokenAmount = getTransactionAmount(row.entity);

        // Return negative value if the transaction is related to consumption, otherwise positive value
        return isConsumedTransaction(row.entity) ||
          isExpiredTransaction(row.entity)
          ? -Math.abs(tokenAmount)
          : Math.abs(tokenAmount);
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },
  };
}
